import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import moment from 'moment';

import {
	STATUS,
} from '../../../common/constants';
import { formatPercentage } from '../../../common/utils/utils';

import Text from '../../components/text/Text';
import { ListItem } from '../../components/list/ListItem';
import { StatusBadge, Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getStatusLabel } from '../../utils/statusLabel';

const getCompletionLabel = (percentageComplete) => {
	if (percentageComplete === -1) {
		return 'No products in purchase order';
	}

	return formatPercentage(percentageComplete);
};

const PurchaseOrderListItem = ({ item, customProps }) => {
	const styles = useThemedStyles(createStyles);
	const { onPressItem, canViewDetail } = customProps || {};

	const handlePress = useCallback(() => {
		if (canViewDetail && onPressItem) {
			onPressItem(item);
		}
	}, [canViewDetail, item, onPressItem]);

	const createdAt = useMemo(() => {
		return item.created ? moment(item.created).format('DD MMM YYYY') : 'Unknown';
	}, [item.created]);

	const statusLabel = getStatusLabel(item.status, 'Pending');
	const completionLabel = getCompletionLabel(item.percentageComplete);
	const sourceLabel = item.source?.name || 'Unknown supplier';
	const destinationLabel = item.destination?.name || 'Unknown warehouse';
	const notDeliveredItemCount = item?.notDeliveredItemCount || 0;

	return (
		<ListItem
			onPress={canViewDetail ? handlePress : undefined}
			style={styles.item}
		>
			<View style={styles.header}>
				<Text style={styles.title}>
					Purchase order {item.identifier || '—'}
				</Text>
				<StatusBadge
					label={statusLabel}
					tone={item.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					size="small"
				/>
			</View>
			{notDeliveredItemCount > 0 && (
				<Tag
					label={`${notDeliveredItemCount} not delivered`}
					tone="warning"
					style={styles.notDelivered}
				/>
			)}

			<Text style={styles.meta}>
				Created: {createdAt}
			</Text>
			<Text style={styles.meta}>
				Supplier: {sourceLabel}
			</Text>
			<Text style={styles.meta}>
				Destination: {destinationLabel}
			</Text>
			<Text style={styles.meta}>
				Completion: {completionLabel}
			</Text>
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xxs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xs,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
	},
	meta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	notDelivered: {
		alignSelf: 'flex-start',
		marginBottom: space.xs,
	},
});

export default PurchaseOrderListItem;
